import React, { useState, useEffect, useCallback } from "react";
import {
  FaTimes,
  FaUserPlus,
  FaUserMinus,
  FaSpinner,
  FaUserFriends,
  FaUsers,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import "./FollowModals.css";

// Individual follower item component
const FollowerItem = React.memo(
  ({
    follower,
    isOwnProfile,
    currentUser,
    onFollow,
    onRemove,
    isFollowing,
    isRemoving,
  }) => (
    <div className="follower-item">
      <Link to={`/profile/${follower.id}`} className="follower-link">
        <img
          src={
            follower.profilePhotoUrl ||
            `https://ui-avatars.com/api/?name=${encodeURIComponent(
              follower.name || "User"
            )}&background=4f46e5&color=fff&size=50`
          }
          alt={follower.name}
          className="follower-avatar"
        />
        <div className="follower-info">
          <h4>{follower.name}</h4>
          <p>
            {follower.professionalRole || "Researcher"}
            {follower.institutionEmail &&
              ` at ${follower.institutionEmail.split("@")[1]}`}
          </p>
          {follower.field && (
            <span className="follower-field">{follower.field}</span>
          )}
        </div>
      </Link>
      {isOwnProfile ? (
        <button
          className={`remove-follower-btn ${isRemoving ? "loading" : ""}`}
          onClick={(e) => {
            e.preventDefault();
            onRemove(follower.id);
          }}
          disabled={isRemoving}
        >
          {isRemoving ? <FaSpinner className="spinner" /> : <FaUserMinus />}
          {isRemoving ? "Removing..." : "Remove"}
        </button>
      ) : (
        follower.id !== currentUser?.id && (
          <button
            className={`follow-back-btn ${isFollowing ? "loading" : ""} ${
              follower.isFollowing ? "following" : ""
            }`}
            onClick={(e) => {
              e.preventDefault();
              onFollow(follower.id);
            }}
            disabled={isFollowing || follower.isFollowing}
          >
            {isFollowing ? <FaSpinner className="spinner" /> : <FaUserPlus />}
            {isFollowing
              ? "Following..."
              : follower.isFollowing
              ? "Following"
              : "Follow"}
          </button>
        )
      )}
    </div>
  )
);

// Individual following item component
const FollowingItem = React.memo(
  ({
    following,
    isOwnProfile,
    onUnfollow,
    onFollowBack,
    isUnfollowing,
    isFollowing,
  }) => (
    <div className="following-item">
      <Link to={`/profile/${following.id}`} className="following-link">
        <img
          src={
            following.profilePhotoUrl ||
            `https://ui-avatars.com/api/?name=${encodeURIComponent(
              following.name || "User"
            )}&background=4f46e5&color=fff&size=50`
          }
          alt={following.name}
          className="following-avatar"
        />
        <div className="following-info">
          <h4>{following.name}</h4>
          <p>
            {following.professionalRole || "Researcher"}
            {following.institutionEmail &&
              ` at ${following.institutionEmail.split("@")[1]}`}
          </p>
          {following.field && (
            <span className="following-field">{following.field}</span>
          )}
        </div>
      </Link>
      {isOwnProfile && (
        <div className="button-container">
          {following.isFollowing !== false ? (
            <button
              className={`unfollow-btn ${isUnfollowing ? "loading" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                onUnfollow(following.id);
              }}
              disabled={isUnfollowing}
            >
              {isUnfollowing ? (
                <FaSpinner className="spinner" />
              ) : (
                <FaUserMinus />
              )}
              {isUnfollowing ? "Unfollowing..." : "Unfollow"}
            </button>
          ) : (
            <button
              className={`follow-back-btn ${isFollowing ? "loading" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                onFollowBack(following.id);
              }}
              disabled={isFollowing}
            >
              {isFollowing ? <FaSpinner className="spinner" /> : <FaUserPlus />}
              {isFollowing ? "Following..." : "Follow"}
            </button>
          )}
        </div>
      )}
    </div>
  )
);

// Main component that exports single modal with tabs
const FollowModals = ({
  showFollowersModal,
  showFollowingModal,
  closeFollowersModal,
  closeFollowingModal,
  stats,
  followersData,
  followingData,
  followersListRef,
  followingListRef,
  scrollPositionRef,
  isOwnProfile,
  currentUser,
  handleFollowFromModal,
  handleUnfollow,
  handleFollowBackFromFollowing,
  handleRemoveFollower,
  unfollowingUsers,
  followingUsers,
  followersLoading,
  followingLoading,
  loadFollowersData,
  loadFollowingData,
}) => {
  const [activeTab, setActiveTab] = useState("followers");
  const [removingUsers, setRemovingUsers] = useState(new Set());
  const showModal = showFollowersModal || showFollowingModal;

  // Handle removing a follower (wrapper to manage loading state)
  const handleRemoveFollowerLocal = async (userId) => {
    try {
      setRemovingUsers((prev) => new Set(prev).add(userId));
      await handleRemoveFollower(userId);
    } catch (error) { /* ignored */ } finally {
      setRemovingUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  // Set active tab based on which modal was opened
  useEffect(() => {
    if (showFollowersModal) {
      setActiveTab("followers");
    } else if (showFollowingModal) {
      setActiveTab("following");
    }
  }, [showFollowersModal, showFollowingModal]);

  // Load data when modal opens - Fixed to prevent infinite loading
  useEffect(() => {
    if (showFollowersModal) {
      loadFollowersData();
    }
  }, [showFollowersModal, loadFollowersData]);

  useEffect(() => {
    if (showFollowingModal) {
      loadFollowingData();
    }
  }, [showFollowingModal, loadFollowingData]);

  // Load data when switching tabs
  const handleTabSwitch = useCallback((newTab) => {
    setActiveTab(newTab);
    // Data loading is handled by the useEffect hooks above
  }, []);

  const closeModal = () => {
    if (showFollowersModal) {
      closeFollowersModal();
    } else if (showFollowingModal) {
      closeFollowingModal();
    }
  };

  if (!showModal) return null;

  const isLoading =
    activeTab === "followers" ? followersLoading : followingLoading;
  const currentData = activeTab === "followers" ? followersData : followingData;

  return (
    <div className="follow-modal-overlay" onClick={closeModal}>
      <div className="follow-modal" onClick={(e) => e.stopPropagation()}>
        <div className="follow-modal-header">
          <h2>
            {activeTab === "followers" ? (
              <>
                <FaUsers /> Followers
              </>
            ) : (
              <>
                <FaUserFriends /> Following
              </>
            )}
          </h2>
          <button className="follow-close-btn" onClick={closeModal}>
            <FaTimes />
          </button>
        </div>

        <div className="follow-modal-tabs">
          <button
            className={`follow-tab-btn ${
              activeTab === "followers" ? "active" : ""
            }`}
            onClick={() => handleTabSwitch("followers")}
          >
            <FaUsers />
            Followers ({stats.followers})
          </button>
          <button
            className={`follow-tab-btn ${
              activeTab === "following" ? "active" : ""
            }`}
            onClick={() => handleTabSwitch("following")}
          >
            <FaUserFriends />
            Following ({stats.following})
          </button>
        </div>

        <div className="follow-modal-content">
          {isLoading ? (
            <div className="loading-state">
              <FaSpinner className="spinner" />
              <p>Loading...</p>
            </div>
          ) : (
            <>
              {activeTab === "followers" && (
                <div
                  className="followers-list"
                  ref={followersListRef}
                  style={{ scrollBehavior: "auto" }}
                  onScroll={(e) => {
                    scrollPositionRef.current = e.target.scrollTop;
                  }}
                >
                  {followersData.length === 0 ? (
                    <div className="empty-state">
                      <FaUsers />
                      <p>No followers yet</p>
                      <small>People who follow you will appear here</small>
                    </div>
                  ) : (
                    followersData.map((follower) => (
                      <FollowerItem
                        key={follower.id}
                        follower={follower}
                        isOwnProfile={isOwnProfile}
                        currentUser={currentUser}
                        onFollow={handleFollowFromModal}
                        onRemove={handleRemoveFollowerLocal}
                        isFollowing={followingUsers.has(follower.id)}
                        isRemoving={removingUsers.has(follower.id)}
                      />
                    ))
                  )}
                </div>
              )}

              {activeTab === "following" && (
                <div
                  className="following-list"
                  ref={followingListRef}
                  style={{ scrollBehavior: "auto" }}
                  onScroll={(e) => {
                    scrollPositionRef.current = e.target.scrollTop;
                  }}
                >
                  {followingData.length === 0 ? (
                    <div className="empty-state">
                      <FaUserFriends />
                      <p>Not following anyone yet</p>
                      <small>People you follow will appear here</small>
                    </div>
                  ) : (
                    followingData.map((following) => (
                      <FollowingItem
                        key={following.id}
                        following={following}
                        isOwnProfile={isOwnProfile}
                        onUnfollow={handleUnfollow}
                        onFollowBack={handleFollowBackFromFollowing}
                        isUnfollowing={unfollowingUsers.has(following.id)}
                        isFollowing={followingUsers.has(following.id)}
                      />
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FollowModals;
